import React, { useContext, useEffect } from "react";
import _ from "lodash";
import cx from 'classnames';

import { useDispatch, useSelector } from "react-redux";

import { CollectionEditState } from "src/types/collections"
import { MenuState } from "../menu"
import Thumbnail from "src/components/thumbnail";

import styles from './customerLogo.module.scss';
import { animated, useTransition } from "@react-spring/web";
import { parseMediaMetadata } from "src/utils/media.service";
import { invalidateMediaUpload, uploadMedia } from "src/redux/actions/media";
import { MediaFile } from "src/types/media";
import { CollectionContext } from "../context";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import { toast } from "react-toastify";

interface CustomerLogoProps {
  className?: string,
  collection: CollectionEditState,
  menuState: MenuState,
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (menuState: MenuState) => void
}

const CustomerLogo: React.FC<CustomerLogoProps> = ({ className, collection, menuState, onMenuChange }) => {
  const dispatch = useDispatch();
  const collectionContext = useContext(CollectionContext);

  const { trackAddLogoClickedEvent, trackAddLogoUploaded } = useCollectionsAdoption();

  const editing = menuState.primaryAction !== 'view';
  const logo = collection.customerLogo;

  const { uploading, uploaded, media } = useSelector((state: any) => {
    const customerLogo = _.get(state, `${collectionContext.editSelector}.customerLogo`);
    const entityType = _.get(customerLogo, 'entityType');
    if (entityType === 'media-preview') {
      const uploading = _.get(state, `media['${customerLogo.id}'].loading`, false);
      const uploaded = _.get(state, `media['${customerLogo.id}'].loaded`, false);
      const media = _.get(state, `media['${customerLogo.id}'].item`);

      return {
        uploading,
        uploaded,
        media,
      }
    }

    return {
      uploading: false,
      uploaded: false,
      media: null,
    };
  }, _.isEqual);

  const logoTransitions = useTransition(logo != null, {
    initial: { opacity: 1, height: 'auto' },
    from: { opacity: 0, height: 0 },
    enter: { opacity: 1, height: 'auto' },
    leave: { opacity: 0, height: 0 },
    config: { duration: 200 }
  });

  const handleRemove = () => {
    collectionContext.updateCollectionProperty('customerLogo', null);
    const fileInput = document.querySelector("input[type='file']") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if(file.size > 2 * 1024 * 1024){
      toast.error("Please upload a file of size less than 2MB");
      return;
    }

    trackAddLogoUploaded(collection.name);

    const customerLogo = await parseMediaMetadata(file);
    collectionContext.updateCollectionProperty('customerLogo', customerLogo);

    uploadCustomerLogo(customerLogo, file);
  }

  const uploadCustomerLogo = (customerLogo, file: File) => {
    dispatch(uploadMedia({
      id: customerLogo?.id as string,
      media: {
        ...customerLogo,
        file: file
      } as MediaFile
    }));
  }

  useEffect(() => {
    if (editing && uploaded) {
      const mediaId = _.get(collection, 'customerLogo.id');
      collectionContext.updateCollectionProperty('customerLogo', media);

      if (mediaId) {
        dispatch(invalidateMediaUpload(mediaId));
      }
    }
  }, [editing, uploaded, media]);

  return (
    logoTransitions((style, item) => item &&
      <animated.div style={style} className={cx(styles.customerLogo, {
        [styles.editing]: editing,
        [styles.active]: uploading,
      }, className)}>
        <Thumbnail className={styles.thumbnail} media={logo} />
        {logo && editing && !uploading &&
          <div className={styles.actions}>
            <button className={cx(styles.action, styles.edit)}>
              <i className='paperflite-edit' />
              <input type="file" accept={'image/*'} 
                disabled={!editing} 
                onChange={handleFileChange}
                onClick={() => trackAddLogoClickedEvent(collection.name)}
              ></input>
            </button>
            <button className={cx(styles.action, styles.edit)} onClick={handleRemove}>
              <i className='paperflite-close' />
            </button>
          </div>
        }

        {logo && editing && uploading &&
          <i className={cx(styles.uploading, 'fa-solid fa-spinner fa-spin-pulse')} />
        }
      </animated.div>
    )
  );
}
export { CustomerLogo }